import { Credentials } from '@quadminds/client-apis/auth';
import { FormikProps } from 'formik';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaApple, FaMicrosoft } from 'react-icons/fa';
import { FcGoogle } from 'react-icons/fc';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import google_icon from '../../assets/google_icon.svg';
import icons_eye from '../../assets/icons_eye.svg';
import { PLATFORM_URL } from '../../config';
import { pickPlatformValue, SaasLegacyLoginErrorCodes } from '../../utils';
import Spinner from '../Spinner/Spinner';

type BaseLoginFormProps = {
    formik: FormikProps<Credentials>;
    isLoading: boolean;
    firebaseError: string;
    legacyLoginError: string;
    showPassword: boolean;
    onTogglePassword: () => void;
    onEmailChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onPasswordChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onEmailBlur: () => void;
    onPasswordBlur: () => void;
    onAutofill: (event: React.AnimationEvent<HTMLInputElement>) => void;
    onGoogleLogin: () => void;
};

type BrandedLoginFormProps = BaseLoginFormProps & {
    onMicrosoftLogin: () => void;
    onAppleLogin: () => void;
};

type ClassicLoginFormProps = BaseLoginFormProps & {
    isSaas: boolean;
};

// The field may hold the user's code (such as 'nsa@operaciones') instead of an email,
// so the branded form never relies on the browser's native email validation
const BRANDED_EMAIL_INPUT_TYPE = 'text';

const BRANDED_INPUT_BASE_CLASS =
    'w-full px-3 py-2 rounded-md border text-white placeholder:text-gray-500 focus:outline-none focus:border-primary font-roboto disabled:bg-gray-700 disabled:border-gray-600 disabled:text-gray-500';

const BRANDED_THIRD_PARTY_BUTTON_CLASS =
    'w-full flex items-center justify-center gap-2 bg-transparent border border-gray-600 text-white hover:bg-gray-800/50 font-poppins py-2.5 rounded-lg transition-colors';

// Returns the error message only when the field was already touched
const visibleError = (touched?: boolean, error?: string): string | undefined => (touched && error ? error : undefined);

const isSubmitDisabled = (formik: FormikProps<Credentials>, isLoading: boolean, legacyLoginError: string): boolean =>
    !(formik.isValid && formik.dirty) || isLoading || !!legacyLoginError;

// Branded (mindy / fleetcare / oms) login form
export const BrandedLoginForm: React.FC<BrandedLoginFormProps> = ({
    formik,
    isLoading,
    firebaseError,
    legacyLoginError,
    showPassword,
    onTogglePassword,
    onEmailChange,
    onPasswordChange,
    onEmailBlur,
    onPasswordBlur,
    onAutofill,
    onGoogleLogin,
    onMicrosoftLogin,
    onAppleLogin,
}) => {
    const { t } = useTranslation();
    const emailError = visibleError(formik.touched.email, formik.errors.email);
    const passwordError = visibleError(formik.touched.password, formik.errors.password) || firebaseError;
    const passwordMessage = formik.errors.password || firebaseError;

    return (
        <div className="w-full">
            {/* Title and description */}
            <div className="mb-6">
                <h2 className="text-2xl font-poppins font-semibold text-white mb-1">{t('login.signIn')}</h2>
                <p className="text-sm text-gray-400 font-roboto">{t('login.signInDescription')}</p>
                <div className="mt-4 border-t border-gray-900"></div>
            </div>

            {/* Form */}
            <form onSubmit={formik.handleSubmit} autoComplete="off" className="space-y-5">
                {/* Third-party sign in */}
                <div className="flex flex-col gap-2">
                    <button
                        type="button"
                        data-testid="loginGoogle"
                        onClick={onGoogleLogin}
                        className={BRANDED_THIRD_PARTY_BUTTON_CLASS}
                    >
                        <FcGoogle className="w-5 h-5" />
                        {t('login.google')}
                    </button>
                    <button
                        type="button"
                        data-testid="loginMicrosoft"
                        onClick={onMicrosoftLogin}
                        className={BRANDED_THIRD_PARTY_BUTTON_CLASS}
                    >
                        <FaMicrosoft className="w-5 h-5 text-blue-400" />
                        {t('login.microsoft')}
                    </button>
                    <button
                        type="button"
                        data-testid="loginApple"
                        onClick={onAppleLogin}
                        className={BRANDED_THIRD_PARTY_BUTTON_CLASS}
                    >
                        <FaApple className="w-5 h-5" />
                        {t('login.apple')}
                    </button>
                </div>

                {/* Divider */}
                <div className="my-5 flex items-center gap-4">
                    <div className="h-px flex-1 bg-gray-800" />
                    <span className="text-sm text-gray-400 font-roboto">{t('login.or')}</span>
                    <div className="h-px flex-1 bg-gray-800" />
                </div>

                {/* Email field */}
                <div>
                    <label htmlFor="email" className="text-white text-sm mb-1.5 block font-poppins">
                        {t('login.emailOrUsernameLabel')}
                    </label>
                    <input
                        id="email"
                        name="login-email"
                        type={BRANDED_EMAIL_INPUT_TYPE}
                        autoComplete="username"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        data-formik-field="email"
                        placeholder={t('login.emailPlaceholder')}
                        value={formik.values.email}
                        onChange={onEmailChange}
                        onBlur={onEmailBlur}
                        onAnimationStart={onAutofill}
                        required
                        className={`${BRANDED_INPUT_BASE_CLASS} ${
                            emailError ? 'border-red-500 bg-gray-900' : 'bg-gray-900 border-gray-700'
                        }`}
                    />
                    <AnimatePresence>
                        {emailError && (
                            <motion.p
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="mt-1 text-sm text-red-400"
                            >
                                {emailError}
                            </motion.p>
                        )}
                    </AnimatePresence>
                </div>

                {/* Password field */}
                <div>
                    <label htmlFor="password" className="text-white text-sm mb-1.5 block font-poppins">
                        {t('login.password')}
                    </label>
                    <div className="relative">
                        <input
                            id="password"
                            name="login-password"
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="current-password"
                            data-formik-field="password"
                            placeholder={t('login.password')}
                            value={formik.values.password}
                            onChange={onPasswordChange}
                            onBlur={onPasswordBlur}
                            onAnimationStart={onAutofill}
                            required
                            className={`${BRANDED_INPUT_BASE_CLASS} ${
                                passwordError ? 'border-red-500 bg-gray-900' : 'bg-gray-900 border-gray-700'
                            }`}
                        />
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                            <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                type="button"
                                onClick={onTogglePassword}
                            >
                                {showPassword ? (
                                    <EyeOff className="h-4 w-4 text-gray-400" />
                                ) : (
                                    <Eye className="h-4 w-4 text-gray-400" />
                                )}
                            </motion.button>
                        </div>
                    </div>
                    <AnimatePresence>
                        {passwordError && (
                            <motion.p
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="mt-1 text-sm text-red-400"
                            >
                                {passwordMessage}
                            </motion.p>
                        )}
                    </AnimatePresence>
                    <div className="mt-2 text-right">
                        <Link
                            to="/recover-password"
                            className="text-sm text-primary-200 hover:text-primary-300 underline transition-colors font-roboto"
                        >
                            {t('login.forgotPassword')}
                        </Link>
                    </div>
                </div>

                {/* Legacy login blocked error */}
                <AnimatePresence>
                    {legacyLoginError === SaasLegacyLoginErrorCodes.BLOCKED && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="bg-red-900/30 border border-red-700/50 rounded-lg p-3"
                        >
                            <p className="text-red-300 text-sm font-roboto">
                                {t('login.error.userBlocked.body')}{' '}
                                <a className="text-primary-200 font-semibold" href="mailto:soporte@quadminds.com">
                                    soporte@quadminds.com
                                </a>
                            </p>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Submit button */}
                <button
                    type="submit"
                    data-testid="loginManual"
                    disabled={isSubmitDisabled(formik, isLoading, legacyLoginError)}
                    className="w-full bg-primary-500 hover:bg-primary-400 disabled:opacity-50 text-white font-poppins font-semibold py-3 rounded-lg transition-colors"
                >
                    {isLoading ? (
                        <div className="flex justify-center">
                            <Spinner color="#FFF" height={24} width={24} />
                        </div>
                    ) : (
                        t('login.login')
                    )}
                </button>
            </form>
        </div>
    );
};

type ClassicCredentialFieldsProps = Pick<
    ClassicLoginFormProps,
    | 'formik'
    | 'firebaseError'
    | 'legacyLoginError'
    | 'showPassword'
    | 'onTogglePassword'
    | 'onEmailChange'
    | 'onPasswordChange'
    | 'onEmailBlur'
    | 'onPasswordBlur'
    | 'onAutofill'
    | 'isSaas'
>;

// Email and password fields of the classic (saas / flash / stork) login form
const ClassicCredentialFields: React.FC<ClassicCredentialFieldsProps> = ({
    formik,
    firebaseError,
    legacyLoginError,
    showPassword,
    onTogglePassword,
    onEmailChange,
    onPasswordChange,
    onEmailBlur,
    onPasswordBlur,
    onAutofill,
    isSaas,
}) => {
    const { t } = useTranslation();
    const emailError = visibleError(formik.touched.email, formik.errors.email);
    const passwordError = visibleError(formik.touched.password, formik.errors.password) || firebaseError;
    const passwordMessage = formik.errors.password || firebaseError;

    return (
        <>
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mb-4"
            >
                <label className="mb-1 block text-sm" htmlFor="email">
                    {isSaas ? t('login.codeOrEmail') : 'Email'}
                </label>
                <input
                    placeholder="Email"
                    name="login-email"
                    type={isSaas ? 'text' : 'email'}
                    autoComplete="off"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    data-formik-field="email"
                    onChange={onEmailChange}
                    value={formik.values.email}
                    onBlur={onEmailBlur}
                    onAnimationStart={onAutofill}
                    className={`input-base bg-white ${
                        emailError && 'border-red-500 focus:border-red-500 hover:border-red-700'
                    }`}
                />
                <AnimatePresence>
                    {emailError && (
                        <motion.h1
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="mt-1 text-sm text-red-500 font-semibold"
                        >
                            {emailError}
                        </motion.h1>
                    )}
                </AnimatePresence>
            </motion.div>
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mb-5"
            >
                <label className="mb-1 block text-sm" htmlFor="password1">
                    {t('login.password')}
                </label>
                <div className="relative">
                    <input
                        placeholder={t('login.password')}
                        type={showPassword ? 'text' : 'password'}
                        name="login-password"
                        autoComplete="current-password"
                        data-formik-field="password"
                        onChange={onPasswordChange}
                        value={formik.values.password}
                        onBlur={onPasswordBlur}
                        onAnimationStart={onAutofill}
                        className={`input-base bg-white ${
                            passwordError ? 'border-red-500 focus:border-red-500 hover:border-red-700' : ''
                        }`}
                    />
                    <div
                        className={`absolute ${
                            passwordError ? 'top-3' : 'inset-y-0'
                        } right-0 pr-3 flex items-center leading-5`}
                    >
                        <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            type="button"
                            onClick={onTogglePassword}
                        >
                            <img src={icons_eye} alt="" />
                        </motion.button>
                    </div>
                    <AnimatePresence>
                        {passwordError && (
                            <motion.p
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                data-testid="pass-error-msj"
                                className="mt-2 text-sm text-red-500 font-semibold"
                            >
                                {passwordMessage}
                            </motion.p>
                        )}
                    </AnimatePresence>
                </div>
                <AnimatePresence>
                    {legacyLoginError === SaasLegacyLoginErrorCodes.BLOCKED && (
                        <p className="mt-2 p-2 pl-auto text-sm bg-red-100 text-gray-900">
                            <span>{t('login.error.userBlocked.body')} </span>
                            <a className="text-primary-500 font-semibold" href="mailto:soporte@quadminds.com">
                                soporte@quadminds.com
                            </a>
                        </p>
                    )}
                </AnimatePresence>
            </motion.div>
        </>
    );
};

// Classic (saas / flash / stork) login form
export const ClassicLoginForm: React.FC<ClassicLoginFormProps> = ({
    formik,
    isLoading,
    firebaseError,
    legacyLoginError,
    showPassword,
    onTogglePassword,
    onEmailChange,
    onPasswordChange,
    onEmailBlur,
    onPasswordBlur,
    onAutofill,
    onGoogleLogin,
    isSaas,
}) => {
    const { t } = useTranslation();

    const loginButtonClass = twMerge(
        'w-full h-[50px] rounded-md text-white font-poppins',
        formik.isValid && formik.dirty ? 'bg-primary-500' : 'bg-primary-200',
    );

    const forgotPasswordClass = twMerge(
        'text-sm underline w-full',
        pickPlatformValue({ branded: 'text-primary-600', stork: 'text-variant-900', fallback: 'text-blue-500' }),
    );

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col justify-center bg-white sm:shadow-xl shadow-slate-500/50 sm:px-16 sm:py-6 2xl:py-9 rounded-md sm:w-full sm:max-w-[480px] h-[544px] sm:max-height-[600px] my-8"
        >
            <h1 className="font-roboto text-3xl font-bold mb-6 sm:mb-7 text-center sm:text-left text-surface-high">
                {t('login.signIn')}
            </h1>
            <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="button"
                className="flex justify-center items-center border-2 text-lg py-2 rounded-md w-full focus:outline-none focus:shadow-outline text-gray-800"
                data-testid="loginGoogle"
                onClick={onGoogleLogin}
            >
                <img src={google_icon} alt="Google icon" className="mr-4" />
                <span>{t('login.google')}</span>
            </motion.button>
            <div className="relative flex my-5 items-center">
                <div className="flex-grow border-t border-gray-300"></div>
                <span className="flex-shrink mx-4 text-gray-300 text-xs">O</span>
                <div className="flex-grow border-t border-gray-300"></div>
            </div>
            <form onSubmit={formik.handleSubmit} autoComplete="off" className="flex flex-col">
                <ClassicCredentialFields
                    formik={formik}
                    firebaseError={firebaseError}
                    legacyLoginError={legacyLoginError}
                    showPassword={showPassword}
                    onTogglePassword={onTogglePassword}
                    onEmailChange={onEmailChange}
                    onPasswordChange={onPasswordChange}
                    onEmailBlur={onEmailBlur}
                    onPasswordBlur={onPasswordBlur}
                    onAutofill={onAutofill}
                    isSaas={isSaas}
                />
                <div className="grid grid-cols-2 items-center">
                    <Link to="/recover-password">
                        <motion.p whileHover={{ scale: 1.05 }} className={forgotPasswordClass}>
                            {t('login.forgotPassword')}
                        </motion.p>
                    </Link>
                </div>
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="py-5"
                >
                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className={loginButtonClass}
                        data-testid="loginManual"
                        type="submit"
                        disabled={isSubmitDisabled(formik, isLoading, legacyLoginError)}
                    >
                        {isLoading ? (
                            <div className="flex justify-center content-center align-middle">
                                <Spinner color="#FFF" height={30} width={30} />
                            </div>
                        ) : (
                            t('login.login')
                        )}
                    </motion.button>
                </motion.div>
                {/* Right now only flash should see signup actions */}
                {PLATFORM_URL?.includes('flash') && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5 }}
                        className="w-full"
                    >
                        <p className="text-md text-center">
                            {t('login.haveAccount')}{' '}
                            <motion.a
                                whileHover={{ scale: 1.05 }}
                                href={`${PLATFORM_URL}/flash`}
                                className="text-blue-600"
                            >
                                {t('signup.register')}
                            </motion.a>
                        </p>
                    </motion.div>
                )}
            </form>
        </motion.div>
    );
};
